import { useRef } from 'preact/hooks';
import type { RefObject } from 'preact';
import { useLocation } from 'wouter-preact';
import { ChevronUp, LogOut, Moon, Settings, Sun, User as UserIcon } from 'lucide-preact';
import { Avatar } from '../display/Avatar';
import { Menu } from '../overlays/Menu';
import { useFloatingState } from '../overlays/Popover';
import { useHoldSidebarWhile } from './sidebarHold';
import { currentUser, logout } from '@/stores/auth';
import { theme, toggleTheme } from '@/stores/theme';
import { cn } from '@/lib/cn';

export interface UserMenuProps {
  /** `sidebar` (default): footer del sidebar con avatar + nombre + role + empresa.
   *  `topbar`: avatar circular standalone en la barra superior (SaaS convention). */
  variant?: 'sidebar' | 'topbar';
  /** Solo aplica a variant=sidebar. */
  collapsed?: boolean;
}

/**
 * UserMenu — entrada al panel de cuenta, settings, switch de empresa y
 * toggle de tema.
 *
 * Dos modos:
 *   - `sidebar` (Linear vibe): footer expandido con avatar + nombre +
 *     role badge + company name. Hover-reveal del frame, chrome invisible
 *     por defecto.
 *   - `topbar` (Stripe / Notion vibe): avatar circular compacto en la
 *     barra superior, click → menu abre hacia abajo-izquierda.
 */
export function UserMenu({ variant = 'sidebar', collapsed }: UserMenuProps) {
  const [, navigate] = useLocation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { open, toggle, close } = useFloatingState(false);
  useHoldSidebarWhile(open);
  const user = currentUser.value;
  if (!user) return null;
  const email = user.email ?? '';
  const name = user.display_name || email.split('@')[0] || 'Usuario';
  const role = user.global_role ?? user.role ?? 'user';
  const isTopbar = variant === 'topbar';
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-label={`Menú de usuario: ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'group/usermenu relative flex items-center text-left outline-none',
          'duration-med ease-smooth transition-[background-color]',
          'focus-visible:shadow-focus focus-visible:ring-brand focus-visible:ring-1',
          isTopbar
            ? // Topbar: avatar compacto circular con hover ring.
              'hover:ring-border h-7 w-7 justify-center rounded-full hover:ring-2'
            : // Sidebar: altura y anchura CONSTANTES (colapsado === expandido).
              // El avatar vive en un slot fijo centrado en el raíl (igual que
              // los iconos del nav) y el texto solo aparece por opacidad, así
              // el footer no cambia de tamaño al expandir.
              cn('h-12 w-full rounded-md', open ? 'bg-surface-2' : 'hover:bg-surface-hover'),
        )}
      >
        {isTopbar ? (
          <AvatarWithStatus name={name} compact />
        ) : (
          <>
            <span className="flex w-10 shrink-0 items-center justify-center">
              <AvatarWithStatus name={name} />
            </span>
            {/* La empresa activa ya vive en el header (WorkspaceSwitcher).
                Aquí solo identidad de usuario: nombre + role. */}
            <span
              className={cn(
                'ease-smooth duration-med flex min-w-0 flex-1 flex-col leading-tight transition-opacity',
                collapsed ? 'pointer-events-none opacity-0' : 'opacity-100',
              )}
            >
              <span className="text-13 text-fg-strong truncate font-medium">{name}</span>
              <span className="text-2xs flex min-w-0 items-center gap-1">
                <RoleBadge role={role} />
              </span>
            </span>
            <ChevronUp
              size={13}
              strokeWidth={2}
              className={cn(
                'ease-smooth duration-med text-fg-faint mr-3 shrink-0 transition-[transform,opacity]',
                collapsed ? 'opacity-0' : 'opacity-100',
                open && 'rotate-180',
              )}
            />
          </>
        )}
      </button>
      <Menu
        open={open}
        onClose={close}
        anchor={triggerRef as RefObject<HTMLElement>}
        placement={isTopbar ? 'bottom-end' : 'top-start'}
        matchAnchorWidth={!isTopbar && !collapsed}
        sections={[
          {
            id: 'main',
            items: [
              {
                id: 'cuenta',
                label: 'Mi cuenta',
                icon: UserIcon,
                onSelect: () => navigate('/cuenta'),
              },
              {
                id: 'config',
                label: 'Configuración',
                icon: Settings,
                onSelect: () => navigate('/configuracion'),
              },
            ],
          },
          // Sección "Empresa activa" se removió: la lógica de cambio
          // de empresa vive ahora en `WorkspaceSwitcher` (header del
          // sidebar).  Tenerla aquí también creaba duplicación.
          {
            id: 'system',
            items: [
              {
                id: 'theme',
                label: theme.value === 'dark' ? 'Tema claro' : 'Tema oscuro',
                icon: theme.value === 'dark' ? Sun : Moon,
                onSelect: () => toggleTheme(),
              },
              {
                id: 'logout',
                label: 'Cerrar sesión',
                icon: LogOut,
                danger: true,
                onSelect: () => {
                  void logout();
                },
              },
            ],
          },
        ]}
      />
    </>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────

function AvatarWithStatus({ name, compact = false }: { name: string; compact?: boolean }) {
  return (
    <span className="relative inline-flex shrink-0">
      <Avatar name={name} size={compact ? 'xs' : 'sm'} />
      {/* Status dot: verde con anillo del color del canvas para que se
          "recorte" del avatar limpiamente. En topbar usamos un dot más
          pequeño para mantener el componente compacto. */}
      <span
        aria-hidden="true"
        className={cn(
          'bg-ok ring-canvas absolute -right-0.5 -bottom-0.5 rounded-full ring-2',
          compact ? 'h-1.5 w-1.5' : 'h-2 w-2',
        )}
      />
    </span>
  );
}

function RoleBadge({ role }: { role: string }) {
  const label = role === 'admin' ? 'Admin' : role === 'manager' ? 'Manager' : 'User';
  const tone =
    role === 'admin'
      ? 'bg-brand-tint text-brand'
      : role === 'manager'
        ? 'bg-accent-tint text-accent'
        : 'bg-surface-2 text-fg-muted';
  return (
    <span
      className={cn(
        'text-3xs tracking-display inline-flex shrink-0 items-center rounded px-1 py-px font-semibold uppercase',
        tone,
      )}
    >
      {label}
    </span>
  );
}
